import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LL } from 'i18n/i18n.service';
import { getReadableAppVersion } from 'environments/version-getters';
import { getSupportEmail } from 'environments/environment-helpers';
import { composeSupportMailtoUrl } from './support-contact-url';

// Build the pre-filled "mailto:" URL used by the "Open Email App" button.
// This is the same subject/body the two support buttons built inline before #3830;
// it now lives in one place. The pure assembly lives in support-contact-url.ts;
// here we just gather the runtime values (subject, version, user agent). The
// support address is passed in by the caller so it is resolved only once per dialog.
// Not exported — the only caller is presentSupportContactAsync below.
function buildSupportMailtoUrl(userEmail: string, supportEmail: string): string {
  return composeSupportMailtoUrl({
    supportEmail,
    subject: LL.profile.reportProblemEmail.subject(),
    userEmail,
    appVersion: getReadableAppVersion(),
    userAgent: window.navigator.userAgent,
  });
}

// Show a dialog that lets the user reach support, instead of relying on a bare
// "mailto:" navigation. On a desktop with no default mail client the old
// "window.location.href = mailto:..." was silently swallowed — no window, no
// error — so the button looked broken (#3830). This dialog always gives a
// visible path: the address is shown as text, can be copied, and the mail app
// can still be opened for users who do have a client.
export async function presentSupportContactAsync(
  alertCtrl: AlertController,
  toastCtrl: ToastController,
  userEmail: string,
): Promise<void> {
  // resolve the support address from the shared, env-aware single source
  const supportEmail = getSupportEmail();
  await alertCtrl.showAlertAsync({
    title: LL.profile.contactSupport.title(),
    // the address is part of the message text, so it stays readable even if the
    // clipboard copy below is blocked by the browser
    message: LL.profile.contactSupport.message({ email: supportEmail }),
    enableBackdropDismiss: true,
    buttons: [
      {
        text: LL.profile.contactSupport.copyAddress(),
        result: async () => {
          try {
            await navigator.clipboard.writeText(supportEmail);
            toastCtrl.showToast({
              message: LL.profile.contactSupport.copied(),
              type: ToastType.SuccessLight,
              duration: 2000,
            });
          } catch (err) {
            // clipboard can be blocked (no user gesture / insecure context);
            // surface it so the user knows to copy the visible address by hand,
            // rather than swallowing the failure silently
            console.error('presentSupportContactAsync: clipboard write failed', err);
            toastCtrl.showToast({
              message: LL.profile.contactSupport.copyFailed(),
              type: ToastType.WarningStrong,
              duration: 3000,
            });
          }
          return undefined;
        },
      },
      {
        text: LL.profile.contactSupport.openMailApp(),
        role: AlertButtonRole.Primary,
        result: () => {
          // keep the one-tap path for users who do have a mail client configured
          window.location.href = buildSupportMailtoUrl(userEmail, supportEmail);
          return undefined;
        },
      },
      {
        text: LL.profile.contactSupport.close(),
        role: AlertButtonRole.Cancel,
        result: undefined,
      },
    ],
  });
}
